import { useMemo, useState } from "react";
import { useLocation } from "wouter";
import { useAuth } from "../../auth/AuthContext";
import { useEnvironments } from "../../api/queries";
import type { Environment } from "../../api/types";
import { EmptyState } from "../../components/AsyncStates";
import { Button } from "../../components/Button";
import { DataTable, type DataTableColumn } from "../../components/DataTable";
import { EnvironmentBadge } from "../../components/EnvironmentBadge";
import { PageHeader } from "../../components/PageHeader";
import { PaginatorControls, usePaginator } from "../../components/Paginator";
import { QueryStates } from "../../components/QueryStates";
import { FleetSubNav } from "../fleet/FleetSubNav";
import { CreateEnvironmentModal } from "./CreateEnvironmentModal";
import "./environments.css";

/** Resolves a source environment id to its name using the environments already
 * on the page, falling back to the raw id if it isn't in view (e.g. paged out).
 * The chain is read-only through this contract, so this is display only. */
function sourceLabel(env: Environment, byId: Map<string, Environment>): string {
  if (!env.sourceEnvId) return "—";
  return byId.get(env.sourceEnvId)?.name ?? env.sourceEnvId;
}

function buildColumns(byId: Map<string, Environment>): DataTableColumn<Environment>[] {
  return [
    { key: "name", header: "Name", cell: (env) => env.name, className: "data-table__primary" },
    { key: "class", header: "Class", cell: (env) => <EnvironmentBadge envClass={env.class} /> },
    {
      key: "requiresPromotion",
      header: "Promotion",
      cell: (env) => (env.requiresPromotion ? "Required" : "Direct deploys"),
      className: "data-table__secondary",
    },
    {
      key: "source",
      header: "Promotes from",
      cell: (env) => sourceLabel(env, byId),
      className: (env) => (env.sourceEnvId ? undefined : "data-table__empty-cell"),
    },
    {
      key: "protected",
      header: "Protected",
      cell: (env) => (env.protected ? "Yes" : "No"),
      className: "data-table__secondary",
    },
  ];
}

export function EnvironmentsListPage() {
  const { session } = useAuth();
  const [, navigate] = useLocation();
  const paginator = usePaginator("environments");
  const { data, isPending, isError, error, refetch } = useEnvironments({ cursor: paginator.cursor });
  const [createOpen, setCreateOpen] = useState(false);

  // Mirrors the authz matrix exactly: environment:create is admin-only
  // (server/internal/authz/model.go). An operator/viewer holds environment:view
  // and sees the list read-only. This is UX only — the server enforces the real
  // decision on every request (S-8).
  const isAdmin = session?.role === "admin";

  const byId = useMemo(() => {
    const map = new Map<string, Environment>();
    for (const env of data?.items ?? []) map.set(env.id, env);
    return map;
  }, [data]);
  const columns = useMemo(() => buildColumns(byId), [byId]);

  const createButton = (
    <Button variant="primary" onClick={() => setCreateOpen(true)}>
      New environment
    </Button>
  );

  return (
    <>
      <FleetSubNav activeTab="environments" />
      <PageHeader
        title="Environments"
        description="Group your machines into staging, production, or however else you separate deploys — and control what's allowed to run where."
        actions={isAdmin ? createButton : <span className="page-header__hint">Ask an admin to add environments.</span>}
      />

      <QueryStates
        isLoading={isPending}
        loadingLabel="Loading environments…"
        isError={isError}
        error={error}
        onRetry={refetch}
        errorDescription={
          error instanceof Error
            ? error.message
            : "Couldn't load environments. Check your connection and try again."
        }
        isEmpty={Boolean(data && data.items.length === 0)}
        empty={
          <EmptyState
            title="No environments yet"
            description="Create an environment to separate staging from production and gate what each admits."
            action={isAdmin ? createButton : undefined}
          />
        }
      >
        {data && (
          <>
            <DataTable
              columns={columns}
              rows={data.items}
              rowKey={(env) => env.id}
              onRowClick={(env) => navigate(`/environments/${env.id}`)}
            />
            <PaginatorControls
              itemCount={data.items.length}
              nextCursor={data.nextCursor}
              paginator={paginator}
              noun="environments"
            />
          </>
        )}
      </QueryStates>

      {isAdmin && <CreateEnvironmentModal open={createOpen} onClose={() => setCreateOpen(false)} />}
    </>
  );
}
